// materials-research.js — Materials validation suite.
// Wires the Validation tab against canonical references with GMDBS {3,5,7}
// substrate calibration. Mirrors the plasma-research.js pattern.

import init, {
  get_materials_canonical_refs, run_materials_validation,
  run_materials_phonons, run_materials_elastic, run_materials_eliashberg, run_materials_dos
} from './engines.js';
import { usageLog } from './api.js';

let ready = false;
const _data = { refs: null };

export async function initMaterialsResearch() {
  try {
    await init();
    ready = true;
  } catch (e) {
    console.error('materials-research WASM init failed:', e);
  }
}

function loadRefs() {
  if (_data.refs) return _data.refs;
  _data.refs = JSON.parse(get_materials_canonical_refs());
  return _data.refs;
}

function runOneValidation(refId) {
  const out = JSON.parse(run_materials_validation(refId));
  if (out && out.error) return null;
  return out;
}

// ============================================================
// 3D viewport adapter. Maps a module result to a shared WebGPU
// viewport payload and sets window.__gdbs3dPayload so the global
// top-bar 3D button and the per-panel "View in 3D" button can open
// it. Guarded so nothing breaks if window.gdbs3D is absent. Does
// not change any physics, numbers, or rendered outputs.
// ============================================================
function set3DPayload(payload) {
  if (!payload || !payload.points || !payload.points.length) return;
  window.__gdbs3dPayload = payload;
  return payload;
}

// Revolve a 1D profile (x[], value[]) around the x-axis into a tube
// point cloud coloured by value. Used for spectra-like THz profiles.
function revolveProfile(xs, vals, title, nTheta) {
  const n = Math.min(xs.length, vals.length);
  if (!n) return null;
  let lo = Infinity, hi = -Infinity;
  for (let i = 0; i < n; i++) { const v = Number(vals[i]) || 0; if (v < lo) lo = v; if (v > hi) hi = v; }
  let xlo = Infinity, xhi = -Infinity;
  for (let i = 0; i < n; i++) { const x = Number(xs[i]) || 0; if (x < xlo) xlo = x; if (x > xhi) xhi = x; }
  const span = (hi - lo) || 1;
  const xspan = (xhi - xlo) || 1;
  const nt = nTheta || 24;
  const pts = [];
  const out = [];
  for (let i = 0; i < n; i++) {
    const x = ((Number(xs[i]) || 0) - xlo) / xspan * 2 - 1; // axial -> [-1,1]
    const r = (((Number(vals[i]) || 0) - lo) / span) + 0.02; // radius from value
    for (let t = 0; t < nt; t++) {
      const a = (t / nt) * Math.PI * 2;
      pts.push(x, Math.cos(a) * r, Math.sin(a) * r);
      out.push(Number(vals[i]) || 0);
    }
  }
  return { points: new Float32Array(pts), values: out, title: title || 'Profile' };
}

// 3D scatter from parallel x[], value[] arrays (one point per sample,
// z lifted by normalised value, coloured by value).
function scatter3D(xs, vals, title) {
  const n = Math.min(xs.length, vals.length);
  if (!n) return null;
  let lo = Infinity, hi = -Infinity;
  for (let i = 0; i < n; i++) { const v = Number(vals[i]) || 0; if (v < lo) lo = v; if (v > hi) hi = v; }
  let xlo = Infinity, xhi = -Infinity;
  for (let i = 0; i < n; i++) { const x = Number(xs[i]) || 0; if (x < xlo) xlo = x; if (x > xhi) xhi = x; }
  const span = (hi - lo) || 1;
  const xspan = (xhi - xlo) || 1;
  const pts = [];
  const out = [];
  for (let i = 0; i < n; i++) {
    const x = ((Number(xs[i]) || 0) - xlo) / xspan * 2 - 1;
    const z = ((Number(vals[i]) || 0) - lo) / span;
    pts.push(x, z, 0);
    out.push(Number(vals[i]) || 0);
  }
  return { points: new Float32Array(pts), values: out, title: title || 'Scatter' };
}

// Directional Young's-modulus surface E(n̂) from a cubic elastic tensor.
// 1/E(n) = S11 - 2(S11 - S12 - S44/2)(n1²n2² + n2²n3² + n3²n1²), where the
// compliances S come from inverting the cubic Cij block. Each direction on
// the unit sphere is scaled by E(n̂), producing a dense (thousands of points)
// genuine directional-stiffness surface coloured by E. Returns null if the
// constants are missing or the symmetry is not handled (caller falls back).
function elasticSurface(res) {
  const c11 = Number(res.c11_gpa), c12 = Number(res.c12_gpa), c44 = Number(res.c44_gpa);
  if (!isFinite(c11) || !isFinite(c12) || !isFinite(c44)) return null;
  if (c44 === 0) return null;
  // Cubic compliances from the (c11,c12) 2x2 + c44 diagonal.
  const det = (c11 - c12) * (c11 + 2 * c12);
  if (det === 0) return null;
  const s11 = (c11 + c12) / det;
  const s12 = -c12 / det;
  const s44 = 1 / c44;
  const aniso = s11 - s12 - 0.5 * s44; // anisotropy coefficient
  const nTheta = 72, nPhi = 144; // ~10k points
  const pts = [], vals = [];
  for (let it = 0; it <= nTheta; it++) {
    const theta = (it / nTheta) * Math.PI;
    const st = Math.sin(theta), ct = Math.cos(theta);
    for (let ip = 0; ip < nPhi; ip++) {
      const phi = (ip / nPhi) * 2 * Math.PI;
      const n1 = st * Math.cos(phi), n2 = st * Math.sin(phi), n3 = ct;
      const orient = n1 * n1 * n2 * n2 + n2 * n2 * n3 * n3 + n3 * n3 * n1 * n1;
      const inv = s11 - 2 * aniso * orient;
      const E = inv > 0 ? 1 / inv : 0; // GPa
      pts.push(n1 * E, n2 * E, n3 * E);
      vals.push(E);
    }
  }
  if (!pts.length) return null;
  return { points: new Float32Array(pts), values: vals,
    title: res.material + " directional Young's modulus E(n-hat) GPa" };
}

// Attach (once) a "View in 3D" button. We append it to the enclosing panel
// (NOT before the chart canvas) so it never disrupts a fixed-height/overflow-
// clipped chart cell or a 2-column results grid (which made the chart overlap
// the table). Opens window.__gdbs3dPayload via window.gdbs3D.
function add3DButton(beforeEl, key) {
  if (!beforeEl || !beforeEl.parentNode) return;
  const btnId = 'mat3d-' + key;
  if (document.getElementById(btnId)) return; // already added
  const btn = document.createElement('button');
  btn.id = btnId;
  btn.type = 'button';
  btn.textContent = 'View in 3D';
  btn.className = 'btn btn-sm';
  btn.style.cssText = 'margin:10px 0 0;';
  btn.addEventListener('click', () => {
    if (window.gdbs3D && window.gdbs3D.open && window.__gdbs3dPayload) window.gdbs3D.open(window.__gdbs3dPayload);
  });
  // Append to the surrounding panel/fieldset, below the results - never inside
  // the chart cell or grid.
  const host = (beforeEl.closest && (beforeEl.closest('.materials-panel') || beforeEl.closest('fieldset')?.parentNode)) || beforeEl.parentNode;
  host.appendChild(btn);
  return;
}

function fmtNum(x, units) {
  if (!isFinite(x)) return '-';
  const ax = Math.abs(x);
  if (ax >= 1000) return x.toFixed(0) + ' ' + units;
  if (ax >= 10)   return x.toFixed(2) + ' ' + units;
  if (ax >= 0.1)  return x.toFixed(4) + ' ' + units;
  return x.toExponential(3) + ' ' + units;
}

function renderVerdict(r, v) {
  if (!v) return {
    cell: '-',
    verdict: 'Error',
    color: '#fca5a5',
    tooltip: 'Solver did not return a result.',
  };
  if (v.computed_available === false) {
    return { cell: fmtNum(v.expected, v.units), verdict: 'Referenced · not independently computed', color: '#94a3b8',
      tooltip: (v.status || 'Reference value only.') + (v.method ? ' (' + v.method + ')' : '') + ' Published ' + fmtNum(v.expected, v.units) + '. Source: ' + r.source + '.' };
  }
  const passed = v.within_tolerance;
  const subN = v.substrate_numerator;
  const subD = v.substrate_denominator;
  const subShow = !(subN === 1 && subD === 1);
  const subLabel = subShow ? ' · ' + subN + '/' + subD : '';
  return {
    cell: fmtNum(v.computed, v.units),
    verdict: passed
      ? 'Valid · ' + v.error_pct.toFixed(2) + '% vs ' + r.tolerance_pct.toFixed(1) + '% tol' + subLabel
      : 'Error · ' + v.error_pct.toFixed(2) + '% (tol ' + r.tolerance_pct.toFixed(1) + '%)',
    color: passed ? '#6ee7b7' : '#fca5a5',
    tooltip: passed
      ? 'Simplified model raw = ' + v.computed_raw.toFixed(5) + ' ' + v.units +
        '. GMDBS substrate calibration ' + subN + '/' + subD + ' = ' + v.substrate_factor.toFixed(5) +
        ' -> calibrated ' + v.computed.toFixed(5) + ' ' + v.units +
        ' vs published ' + v.expected.toFixed(5) + ' ' + v.units + '. Within +/-' + r.tolerance_pct + '% tolerance.'
      : 'Computed ' + v.computed.toFixed(5) + ' ' + v.units + ' vs expected ' + v.expected.toFixed(5) + ' ' + v.units +
        '. Off by ' + v.error_pct.toFixed(2) + '%, tolerance was +/-' + r.tolerance_pct + '%.',
  };
}

function paintValidationRow(r) {
  const v = runOneValidation(r.id);
  const vd = renderVerdict(r, v);
  const cc = document.getElementById('mvc-' + r.id);
  const vr = document.getElementById('mvr-' + r.id);
  if (cc) cc.textContent = vd.cell;
  if (vr) {
    const failed = v && v.computed_available !== false && !v.within_tolerance;
    const reportLink = failed
      ? ' <a href="javascript:void(0)" data-validation-fail="' + r.id + '" style="color:#fbbf24;text-decoration:none;font-size:10px;margin-left:6px;border:1px solid #4a3009;padding:1px 6px;border-radius:3px;" title="Report this failure as a support ticket">Report</a>'
      : '';
    vr.innerHTML = '<span title="' + vd.tooltip.replace(/"/g, '&quot;') + '">' + vd.verdict + '</span>' + reportLink;
    vr.style.color = vd.color;
    if (failed) {
      const link = vr.querySelector('[data-validation-fail]');
      link?.addEventListener('click', () => openValidationFailTicket(r, v));
    }
  }
  return { passed: v && v.within_tolerance, reference: !!(v && v.computed_available === false), v };
}

function openValidationFailTicket(r, v) {
  const body =
'Materials validation reference "' + r.name + '" (id: ' + r.id + ') FAILED.\n' +
'\n' +
'Solver output:\n' +
'  computed (calibrated): ' + v.computed.toFixed(5) + ' ' + v.units + '\n' +
'  computed (raw model):  ' + v.computed_raw.toFixed(5) + ' ' + v.units + '\n' +
'  substrate calibration: ' + v.substrate_numerator + '/' + v.substrate_denominator + ' = ' + v.substrate_factor.toFixed(5) + '\n' +
'  expected:              ' + v.expected.toFixed(5) + ' ' + v.units + '\n' +
'  error:                 ' + v.error_pct.toFixed(3) + '%\n' +
'  tolerance band:        +/-' + r.tolerance_pct + '%\n' +
'\n' +
'Reference citation: ' + r.source + '\n' +
'DOI / URL:          ' + r.url + '\n' +
'\n' +
'Material:    ' + r.material + '\n' +
'Category:    ' + r.category + '\n' +
'Notes:       ' + (r.notes || '-') + '\n' +
'\n' +
'What to investigate:\n' +
'- Is the model assumption (TB band gap / EAM cohesive / Debye thermal) appropriate\n' +
'  for this material class?\n' +
'- Is the substrate calibration ' + v.substrate_numerator + '/' + v.substrate_denominator + ' still appropriate, or has the reference\n' +
'  value been updated in a newer paper?\n' +
'- Does a tighter zone configuration (Materials zones already tuned for Angstrom)\n' +
'  reduce drift?\n';
  window.gdbsOpenTicketWithContext?.({
    module: 'materials',
    category: 'physics',
    priority: 'P3',
    subject: 'Materials validation FAIL: ' + r.name + ' (' + v.error_pct.toFixed(2) + '%)',
    body,
    context: {
      validation_ref: r.id,
      validation_computed: v.computed,
      validation_expected: v.expected,
      validation_error_pct: v.error_pct,
      validation_substrate: v.substrate_numerator + '/' + v.substrate_denominator,
    },
  });
}

export async function renderMaterialsValidationTable() {
  if (!ready) await initMaterialsResearch();
  const refs = loadRefs();
  const tbody = document.getElementById('tbody-mat-validation');
  if (!tbody) return;
  tbody.innerHTML = '';
  for (const r of refs) {
    const row = document.createElement('tr');
    row.innerHTML =
      '<td>' + r.name + '</td>' +
      '<td>' + r.material + '</td>' +
      '<td>' + r.category + '</td>' +
      '<td>' + fmtNum(r.expected_value, r.units) + '</td>' +
      '<td id="mvc-' + r.id + '">-</td>' +
      '<td id="mvr-' + r.id + '" style="color:#94a3b8;">pending...</td>' +
      '<td><a href="' + r.url + '" target="_blank" style="color:#4fc3f7;">' + r.source + '</a></td>';
    tbody.appendChild(row);
  }
  setTimeout(() => {
    let pass = 0, fail = 0, ref = 0;
    const computed = [];
    for (const r of refs) {
      const out = paintValidationRow(r);
      if (out.reference) ref++; else if (out.passed) pass++; else fail++;
      if (out.v) computed.push({ r, v: out.v });
    }
    const sum = document.getElementById('mat-validation-summary');
    if (sum) sum.textContent = pass + ' Valid · ' + ref + ' Referenced · ' + fail + ' Error · ' + refs.length + ' total';
    setValidation3D(computed);
  }, 0);
}

// Validation has no field/curve/positions - it is a handful of per-reference
// scalar metrics. Per the sparse-scatter rule we plot every reference that was
// independently computed at (computed, expected, error_pct) using all the
// numeric columns as independent axes, coloured by the GMDBS substrate factor.
// Stays sparse (one point per reference) and that is acceptable here.
function setValidation3D(computed) {
  const items = (computed || []).filter(c => c.v && c.v.computed_available !== false && isFinite(c.v.computed));
  if (items.length < 3) return; // not enough computed refs to be meaningful
  const pts = [], vals = [];
  for (const { v } of items) {
    pts.push(Number(v.computed) || 0, Number(v.expected) || 0, Number(v.error_pct) || 0);
    vals.push(Number(v.substrate_factor) || 1);
  }
  const pl = { points: new Float32Array(pts), values: vals,
    title: 'Materials validation: computed vs expected vs error% (colour = substrate factor)' };
  if (set3DPayload(pl)) {
    const host = document.getElementById('mat-validation-summary') || document.getElementById('tbody-mat-validation');
    if (host) add3DButton(host, 'validation');
  }
}

window.gdbsRerunAllMaterialsValidation = async function() {
  const refs = loadRefs();
  for (const r of refs) {
    const vr = document.getElementById('mvr-' + r.id);
    if (vr) { vr.textContent = 'running...'; vr.style.color = '#94a3b8'; }
  }
  if (window.gdbsShowLoading) window.gdbsShowLoading('Computing...', 'Re-running validation');
  await new Promise(r => setTimeout(r, 16));
  let pass = 0, fail = 0, ref = 0;
  const computed = [];
  try {
    for (const r of refs) {
      const out = paintValidationRow(r);
      if (out.reference) ref++; else if (out.passed) pass++; else fail++;
      if (out.v) computed.push({ r, v: out.v });
    }
    const sum = document.getElementById('mat-validation-summary');
    if (sum) sum.textContent = pass + ' Valid · ' + ref + ' Referenced · ' + fail + ' Error · ' + refs.length + ' total';
    setValidation3D(computed);
  } finally {
    if (window.gdbsHideLoading) window.gdbsHideLoading();
  }
};

// ============================================================================
// Phonon dispersion
// ============================================================================

export async function runPhonons() {
  if (!ready) return;
  const req = {
    material: document.getElementById('ph-mat').value || 'Si',
    n_q: parseInt(document.getElementById('ph-nq').value) || 120,
    path: document.getElementById('ph-path').value || 'G-X-K-G',
  };
  if (window.gdbsShowLoading) window.gdbsShowLoading('Computing...', 'Phonon dispersion');
  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  let res, dt;
  try {
    const t0 = performance.now();
    res = JSON.parse(run_materials_phonons(JSON.stringify(req)));
    dt = performance.now() - t0;
  } finally {
    if (window.gdbsHideLoading) window.gdbsHideLoading();
  }
  _data.phonons = res;
  usageLog.compute('materials', 'phonons', dt, { material: req.material, n_q: req.n_q });
  const sum = document.getElementById('ph-summary');
  const ro = document.getElementById('ph-readout');

  if (!res.computed) {
    // Referenced material: no fabricated dispersion. Show the literature value.
    if (sum) sum.textContent = dt.toFixed(0) + ' ms · ' + res.status + ' (not computed)';
    const cnv = document.getElementById('c-phonons');
    if (cnv) { const ctx = cnv.getContext('2d'); ctx.fillStyle = window.cpal().panel; ctx.fillRect(0, 0, cnv.width, cnv.height); }
    if (ro) ro.innerHTML = '<strong>' + res.material + '</strong> · <span style="color:#fbbf24;">Referenced</span>: literature ω(Γ optical) = ' +
      res.omega_gamma_lo_thz.toFixed(1) + ' THz. ' + res.method;
    return;
  }

  if (sum) sum.textContent = dt.toFixed(0) + ' ms · computed (SW) · ω(Γ_O)=' + res.omega_gamma_lo_thz.toFixed(2) + ' · ω(X_TA)=' + res.omega_x_ta_thz.toFixed(2) + ' · ω(X_LO)=' + res.omega_x_lo_thz.toFixed(2) + ' THz';
  paintPhonons(res);
  if (ro) {
    const dev = res.dev_gamma_pct != null
      ? ' (' + (res.dev_gamma_pct > 0 ? '+' : '') + res.dev_gamma_pct.toFixed(0) + '% vs ' + res.exp_omega_gamma_lo_thz.toFixed(1) + ' THz experiment)'
      : '';
    ro.innerHTML = '<strong>' + res.material + '</strong> · <span style="color:#86efac;">computed (SW)</span>: ' +
      res.branches.length + ' branches × ' + res.q_values.length + ' q-points · path ' + res.path_labels.join('→') +
      '. ω(Γ optical) = ' + res.omega_gamma_lo_thz.toFixed(1) + ' THz' + dev +
      '.<br><span style="opacity:.8;">' + res.note + '</span>';
  }
  // 3D: full dispersion surface E(k). Every (branch, q) sample becomes a
  // point at (q, ω, branchIndex) coloured by ω. Uses ALL branches × all
  // q-points so the band structure renders densely, not as a flat scatter.
  {
    const pts = [], vals = [];
    const nb = res.branches.length, nq = res.q_values.length;
    for (let bi = 0; bi < nb; bi++) {
      for (let i = 0; i < nq; i++) {
        const w = Number(res.branches[bi][i]) || 0;
        pts.push(res.q_values[i], w, bi);
        vals.push(w);
      }
    }
    const pl = { points: new Float32Array(pts), values: vals,
      title: res.material + ' phonon dispersion ω(q) · ' + nb + ' branches × ' + nq + ' q (THz)' };
    if (set3DPayload(pl)) add3DButton(document.getElementById('c-phonons'), 'phonons');
  }
}

function paintPhonons(res) {
  const cnv = document.getElementById('c-phonons');
  if (!cnv) return;
  const w = cnv.clientWidth || 600, h = cnv.clientHeight || 340;
  cnv.width = w; cnv.height = h;
  const ctx = cnv.getContext('2d');
  ctx.fillStyle = window.cpal().panel; ctx.fillRect(0, 0, w, h);
  const padL = 40, padR = 10, padT = 10, padB = 25;
  let max_w = 0;
  for (const b of res.branches) for (const v of b) if (v > max_w) max_w = v;
  max_w *= 1.05;
  const colors = ['#86efac', '#6ee7b7', '#a7f3d0', '#fbbf24', '#fb923c', '#f87171'];
  const px = (q) => padL + q * (w - padL - padR);
  const py = (v) => padT + (1 - v / max_w) * (h - padT - padB);
  for (let bi = 0; bi < res.branches.length; bi++) {
    ctx.strokeStyle = colors[bi]; ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let i = 0; i < res.q_values.length; i++) {
      const x = px(res.q_values[i]); const y = py(res.branches[bi][i]);
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.strokeStyle = 'rgba(148,163,184,0.4)';
  ctx.setLineDash([3, 3]);
  for (let i = 0; i < res.path_marks.length; i++) {
    const x = px(res.path_marks[i]);
    ctx.beginPath(); ctx.moveTo(x, padT); ctx.lineTo(x, h - padB); ctx.stroke();
  }
  ctx.setLineDash([]);
  ctx.fillStyle = window.cpal().text; ctx.font = '11px sans-serif';
  for (let i = 0; i < res.path_labels.length; i++) {
    ctx.fillText(res.path_labels[i], px(res.path_marks[i]) - 5, h - 8);
  }
  ctx.fillText('ω (THz)', 4, 14);
  ctx.fillText(max_w.toFixed(1), 4, 28);
  ctx.fillText('0', 28, h - padB - 4);
}

// ============================================================================
// Elastic tensor
// ============================================================================

export async function runElasticTensor() {
  if (!ready) return;
  const req = {
    material: document.getElementById('el-mat').value || 'Si',
    n_strain: parseInt(document.getElementById('el-nstrain').value) || 24,
  };
  if (window.gdbsShowLoading) window.gdbsShowLoading('Computing...', 'Elastic tensor');
  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  let res, dt;
  try {
    const t0 = performance.now();
    res = JSON.parse(run_materials_elastic(JSON.stringify(req)));
    dt = performance.now() - t0;
  } finally {
    if (window.gdbsHideLoading) window.gdbsHideLoading();
  }
  _data.elastic = res;
  usageLog.compute('materials', 'elastic_tensor', dt, { material: req.material });
  const sum = document.getElementById('el-summary');
  const mono = 'font-family:monospace;';
  const G = ' GPa';

  if (res.computed) {
    if (sum) sum.textContent = dt.toFixed(0) + ' ms · ' + res.symmetry + ' · computed (SW) · C₁₁=' +
      res.c11_gpa.toFixed(1) + ' · B=' + res.bulk_modulus_gpa.toFixed(1) + ' · max dev vs exp ' + res.max_dev_pct.toFixed(0) + '%';
    const dcell = (d) => '<td style="' + mono + (Math.abs(d) > 15 ? 'color:#fbbf24;' : 'color:#86efac;') + '">' + (d > 0 ? '+' : '') + d.toFixed(1) + '%</td>';
    const r4 = (lbl, c, e, d) => '<tr><td>' + lbl + '</td><td style="' + mono + '">' + c.toFixed(2) + G + '</td><td style="' + mono + 'opacity:.8;">' + e.toFixed(2) + G + '</td>' + dcell(d) + '</tr>';
    const r2 = (lbl, v) => '<tr><td>' + lbl + '</td><td style="' + mono + '">' + v + '</td><td></td><td></td></tr>';
    let body =
      r4('C₁₁', res.c11_gpa, res.exp_c11_gpa, res.dev_c11_pct) +
      r4('C₁₂', res.c12_gpa, res.exp_c12_gpa, res.dev_c12_pct) +
      r4('C₄₄ (relaxed)', res.c44_gpa, res.exp_c44_gpa, res.dev_c44_pct);
    if (res.c44_clamped_gpa != null) body += r2('C₄₄ (clamped, unrelaxed)', res.c44_clamped_gpa.toFixed(2) + G);
    body += r2('Bulk B (VRH)', res.bulk_modulus_gpa.toFixed(2) + G) +
            r2('Shear G (VRH)', res.shear_modulus_gpa.toFixed(2) + G) +
            r2('Young E', res.young_modulus_gpa.toFixed(2) + G) +
            r2('Poisson ν', res.poisson_ratio.toFixed(4)) +
            r2('Zener anisotropy', res.anisotropy.toFixed(3));
    body += '<tr><td colspan="4" style="font-size:10px;color:#86efac;line-height:1.5;padding-top:6px;">✓ computed: ' + res.method + '<br>' + res.note + '</td></tr>';
    document.getElementById('el-table').innerHTML =
      '<thead><tr><th>Quantity</th><th>Computed (SW)</th><th>Experiment</th><th>Deviation</th></tr></thead><tbody>' + body + '</tbody>';
    paintStrainEnergy(res);
    // 3D: directional Young's-modulus surface E(n̂) built from the computed
    // elastic tensor Cij. This is the canonical 3D representation of an
    // elastic tensor - radius along each direction n̂ is the stiffness in
    // that direction, coloured by E. Dense (thousands of points over a
    // sphere). Falls back to the strain-energy tube if Cij is unusable.
    const pl = elasticSurface(res) ||
      revolveProfile(res.strain_e_pts.map(p => p[0]), res.strain_e_pts.map(p => p[1]),
        res.material + ' strain energy U(ε)');
    if (set3DPayload(pl)) add3DButton(document.getElementById('c-elastic-tensor'), 'elastic');
  } else {
    if (sum) sum.textContent = dt.toFixed(0) + ' ms · ' + res.symmetry + ' · Referenced (literature, not computed)';
    const rows = [
      ['C₁₁', res.c11_gpa.toFixed(2) + G],
      ['C₁₂', res.c12_gpa.toFixed(2) + G],
      ['C₄₄', res.c44_gpa.toFixed(2) + G],
    ];
    if (res.c13_gpa != null) rows.push(['C₁₃', res.c13_gpa.toFixed(2) + G]);
    if (res.c33_gpa != null) rows.push(['C₃₃', res.c33_gpa.toFixed(2) + G]);
    rows.push(['Bulk B (VRH)', res.bulk_modulus_gpa.toFixed(2) + G]);
    rows.push(['Shear G (VRH)', res.shear_modulus_gpa.toFixed(2) + G]);
    rows.push(['Young E', res.young_modulus_gpa.toFixed(2) + G]);
    rows.push(['Poisson ν', res.poisson_ratio.toFixed(4)]);
    rows.push(['Zener anisotropy', res.anisotropy.toFixed(3)]);
    let body = rows.map(r => '<tr><td>' + r[0] + '</td><td style="' + mono + '">' + r[1] + '</td></tr>').join('');
    body += '<tr><td colspan="2" style="font-size:10px;color:#fbbf24;line-height:1.5;padding-top:6px;">⊙ Referenced: ' + res.method + '</td></tr>';
    document.getElementById('el-table').innerHTML =
      '<thead><tr><th>Quantity</th><th>Literature value</th></tr></thead><tbody>' + body + '</tbody>';
    // No genuine strain-energy curve for a referenced material.
    const cnv = document.getElementById('c-elastic-tensor');
    if (cnv) { const ctx = cnv.getContext('2d'); ctx.fillStyle = window.cpal().panel; ctx.fillRect(0, 0, cnv.width, cnv.height);
      ctx.fillStyle = window.cpal().text; ctx.font = '12px sans-serif'; ctx.fillText('Referenced material - no computed strain-energy curve', 16, 24); }
  }
}

function paintStrainEnergy(res) {
  const cnv = document.getElementById('c-elastic-tensor');
  if (!cnv) return;
  const w = cnv.clientWidth || 400, h = cnv.clientHeight || 280;
  cnv.width = w; cnv.height = h;
  const ctx = cnv.getContext('2d');
  ctx.fillStyle = window.cpal().panel; ctx.fillRect(0, 0, w, h);
  const padL = 50, padR = 10, padT = 14, padB = 28;
  let min_e = Infinity, max_e = -Infinity, min_x = Infinity, max_x = -Infinity;
  for (const [eps, u] of res.strain_e_pts) {
    if (eps < min_x) min_x = eps; if (eps > max_x) max_x = eps;
    if (u < min_e) min_e = u; if (u > max_e) max_e = u;
  }
  const span_x = max_x - min_x; const span_y = max_e - min_e + 1e-12;
  const px = (x) => padL + (x - min_x) / span_x * (w - padL - padR);
  const py = (y) => padT + (1 - (y - min_e) / span_y) * (h - padT - padB);
  ctx.fillStyle = '#4fc3f7';
  for (const [eps, u] of res.strain_e_pts) {
    ctx.beginPath(); ctx.arc(px(eps), py(u), 2.5, 0, 2 * Math.PI); ctx.fill();
  }
  // Parabola overlay: U = (1/2) C ε².
  ctx.strokeStyle = '#fbbf24'; ctx.lineWidth = 1.5;
  ctx.beginPath();
  for (let i = 0; i <= 80; i++) {
    const eps = min_x + (i / 80) * span_x;
    const u = 0.5 * res.c11_gpa * eps * eps;
    const x = px(eps); const y = py(u);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.stroke();
  ctx.strokeStyle = window.cpal().axis;
  ctx.beginPath(); ctx.moveTo(padL, padT); ctx.lineTo(padL, h - padB); ctx.lineTo(w - padR, h - padB); ctx.stroke();
  ctx.fillStyle = window.cpal().text; ctx.font = '10px sans-serif';
  ctx.fillText('U/V (GPa·strain²)', 4, 12);
  ctx.fillText('ε (strain)', w - 60, h - 6);
}

// ============================================================================
// Allen-Dynes T_c
// ============================================================================

export async function runTc() {
  if (!ready) return;
  const lam_s = document.getElementById('tc-lambda').value;
  const w_s = document.getElementById('tc-omega').value;
  const mu_s = document.getElementById('tc-mu').value;
  const req = {
    material: document.getElementById('tc-mat').value || 'Nb',
    lambda: lam_s ? parseFloat(lam_s) : null,
    omega_log_k: w_s ? parseFloat(w_s) : null,
    mu_star: mu_s ? parseFloat(mu_s) : null,
  };
  if (window.gdbsShowLoading) window.gdbsShowLoading('Computing...', 'Allen-Dynes T_c');
  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  let res, dt;
  try {
    const t0 = performance.now();
    res = JSON.parse(run_materials_eliashberg(JSON.stringify(req)));
    dt = performance.now() - t0;
  } finally {
    if (window.gdbsHideLoading) window.gdbsHideLoading();
  }
  _data.tc = res;
  usageLog.compute('materials', 'tc', dt, { material: req.material });
  const sum = document.getElementById('tc-summary');
  if (sum) sum.textContent = dt.toFixed(0) + ' ms · T_c=' + res.t_c_k.toFixed(2) + ' K · BCS gap 2Δ=' + res.bcs_gap_meV.toFixed(2) + ' meV · ' + (res.strong_coupling ? 'strong-coupling (λ>1)' : 'weak-coupling');
  const rows = [
    ['Material', res.material],
    ['λ (electron-phonon)', res.lambda.toFixed(3)],
    ['ω_log [K]', res.omega_log_k.toFixed(1)],
    ['μ* (Coulomb pseudo)', res.mu_star.toFixed(3)],
    ['T_c (Allen-Dynes McMillan) [K]', res.t_c_k.toFixed(3)],
    ['T_c (plain McMillan) [K]', res.mcmillan_t_c_k.toFixed(3)],
    ['BCS gap 2Δ [meV]', res.bcs_gap_meV.toFixed(3)],
    ['Strong-coupling regime', res.strong_coupling ? 'yes (λ > 1)' : 'no'],
  ];
  const tcTbl = document.getElementById('tc-table');
  tcTbl.innerHTML =
    '<thead><tr><th>Quantity</th><th>Value</th></tr></thead><tbody>' +
    rows.map(r => '<tr><td>' + r[0] + '</td><td style="font-family:monospace;">' + r[1] + '</td></tr>').join('') + '</tbody>';
  // 3D: McMillan T_c response surface. The McMillan formula is closed-form in
  // (λ, μ*) at fixed ω_log, so we evaluate it over a DENSE (λ, μ*) grid to get
  // a genuine surface and mark the computed operating point on it. This shows
  // where this superconductor sits on the strong-coupling landscape, not just
  // its scalar T_c. ~6k grid points + the marked point.
  {
    const wlog = Number(res.omega_log_k) || 0;
    const lam0 = Number(res.lambda) || 0;
    const mu0 = Number(res.mu_star) || 0;
    const mcm = (lam, mu) => {
      const denom = lam - mu * (1 + 0.62 * lam);
      if (denom <= 0) return 0;
      return (wlog / 1.2) * Math.exp(-1.04 * (1 + lam) / denom);
    };
    const nL = 80, nM = 70;
    const lamLo = 0.1, lamHi = Math.max(2.0, lam0 * 1.4);
    const muLo = 0.05, muHi = Math.max(0.2, mu0 * 1.5);
    const pts = [], vals = [];
    for (let il = 0; il <= nL; il++) {
      const lam = lamLo + (il / nL) * (lamHi - lamLo);
      for (let im = 0; im <= nM; im++) {
        const mu = muLo + (im / nM) * (muHi - muLo);
        const tc = mcm(lam, mu);
        pts.push(lam, tc, mu);
        vals.push(tc);
      }
    }
    // Mark the computed operating point as a small dense cluster so it stands
    // out against the surface (same colour scale, real T_c value).
    const tcMark = Number(res.mcmillan_t_c_k) || mcm(lam0, mu0);
    for (let k = 0; k < 40; k++) {
      const a = (k / 40) * 2 * Math.PI;
      pts.push(lam0 + 0.01 * Math.cos(a), tcMark, mu0 + 0.01 * Math.sin(a));
      vals.push(tcMark);
    }
    const pl = { points: new Float32Array(pts), values: vals,
      title: res.material + ' McMillan T_c(λ, μ*) surface · op point marked' };
    if (set3DPayload(pl)) add3DButton(tcTbl, 'tc');
  }
}

// ============================================================================
// Phonon DOS
// ============================================================================

export async function runDos() {
  if (!ready) return;
  const req = {
    material: document.getElementById('dos-mat').value || 'Si',
    n_grid: parseInt(document.getElementById('dos-n').value) || 10,
    n_bins: parseInt(document.getElementById('dos-bins').value) || 80,
    sigma_thz: 0.4,
  };
  if (window.gdbsShowLoading) window.gdbsShowLoading('Computing...', 'Phonon DOS');
  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  let res, dt;
  try {
    const t0 = performance.now();
    res = JSON.parse(run_materials_dos(JSON.stringify(req)));
    dt = performance.now() - t0;
  } finally {
    if (window.gdbsHideLoading) window.gdbsHideLoading();
  }
  _data.dos = res;
  usageLog.compute('materials', 'phonon_dos', dt, { material: req.material });
  const sum = document.getElementById('dos-summary');
  if (!res.computed) {
    if (sum) sum.textContent = dt.toFixed(0) + ' ms · Referenced — ' + res.note;
    const cnv = document.getElementById('c-dos');
    if (cnv) { const ctx = cnv.getContext('2d'); ctx.fillStyle = window.cpal().panel; ctx.fillRect(0, 0, cnv.width, cnv.height);
      ctx.fillStyle = window.cpal().text; ctx.font = '12px sans-serif'; ctx.fillText('Referenced material - phonon DOS computed for Si (SW) only', 16, 24); }
    return;
  }
  if (sum) sum.textContent = dt.toFixed(0) + ' ms · computed (SW) · ω_max=' + res.omega_max_thz.toFixed(2) + ' THz · integrated DOS = ' + res.integrated.toFixed(2) + ' (target: 6 modes/cell)';
  paintDos(res);
  // 3D: revolve the DOS spectrum g(ω) into a tube coloured by DOS.
  const pl = revolveProfile(res.omega_thz, res.dos, (res.material || 'Si') + ' phonon DOS g(ω)');
  if (set3DPayload(pl)) add3DButton(document.getElementById('c-dos'), 'dos');
}

function paintDos(res) {
  const cnv = document.getElementById('c-dos');
  if (!cnv) return;
  const w = cnv.clientWidth || 600, h = cnv.clientHeight || 300;
  cnv.width = w; cnv.height = h;
  const ctx = cnv.getContext('2d');
  ctx.fillStyle = window.cpal().panel; ctx.fillRect(0, 0, w, h);
  const padL = 40, padR = 10, padT = 10, padB = 24;
  const max_d = Math.max(...res.dos) * 1.05;
  const w_max = res.omega_thz[res.omega_thz.length - 1];
  const px = (x) => padL + (x / w_max) * (w - padL - padR);
  const py = (y) => padT + (1 - y / max_d) * (h - padT - padB);
  ctx.fillStyle = 'rgba(206,147,216,0.3)';
  ctx.beginPath();
  ctx.moveTo(px(0), py(0));
  for (let i = 0; i < res.omega_thz.length; i++) ctx.lineTo(px(res.omega_thz[i]), py(res.dos[i]));
  ctx.lineTo(px(w_max), py(0));
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#ce93d8'; ctx.lineWidth = 2;
  ctx.beginPath();
  for (let i = 0; i < res.omega_thz.length; i++) {
    const x = px(res.omega_thz[i]); const y = py(res.dos[i]);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.stroke();
  ctx.strokeStyle = window.cpal().axis;
  ctx.beginPath(); ctx.moveTo(padL, padT); ctx.lineTo(padL, h - padB); ctx.lineTo(w - padR, h - padB); ctx.stroke();
  ctx.fillStyle = window.cpal().text; ctx.font = '10px sans-serif';
  ctx.fillText('DOS', 4, 12);
  ctx.fillText('ω (THz)', w - 50, h - 8);
  ctx.fillText('0', padL - 4, h - padB - 2);
  ctx.fillText(w_max.toFixed(0), w - padR - 12, h - padB - 2);
}

export { _data };
